import React from "react";

/** Scope row titles: "01 — Title", single-digit OK, common dash variants. */
const SCOPE_ITEM_LINE = /^(\d{1,2})\s*(?:—|–|-|:)\s*(.+)$/;

export function plainTextFromChildren(children) {
  return React.Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child);
      if (React.isValidElement(child)) return plainTextFromChildren(child.props.children);
      return "";
    })
    .join("")
    .trim();
}

/** Plain-text match for scope item titles (used by paragraph / strong / list renderers). */
export function scopeItemTitleMatch(text) {
  const raw = String(text || "").trim();
  let m = raw.match(SCOPE_ITEM_LINE);
  if (m) return m;
  const head = raw.split(/\r?\n/)[0]?.trim() || "";
  return head.match(SCOPE_ITEM_LINE);
}

/** First line vs body after first newline (trim leading blank lines from body). */
export function splitScopeTitleBody(text) {
  const trimmed = String(text || "").trim();
  const nl = trimmed.search(/\r?\n/);
  if (nl === -1) return { head: trimmed, rest: "" };
  const head = trimmed.slice(0, nl).trim();
  const rest = trimmed.slice(nl + 1).replace(/^[\r\n]+/, "").trim();
  return { head, rest };
}

const BODY_CLASS = "mb-2 last:mb-0 font-light text-[var(--t-text)] whitespace-pre-wrap";

export function renderScopeAwareParagraph(children, className = "mb-2 last:mb-0") {
  const text = plainTextFromChildren(children);
  const { head, rest } = splitScopeTitleBody(text);
  const match = head.match(SCOPE_ITEM_LINE) || scopeItemTitleMatch(head);
  if (match) {
    const titleBlock = (
      <p className={`${className} font-semibold text-[#001EFF] ${rest ? "mb-1" : ""}`}>
        <span>{match[1]}</span>
        <span className="mx-1 font-normal opacity-80">—</span>
        <span>{match[2]}</span>
      </p>
    );
    if (!rest) return titleBlock;
    return (
      <>
        {titleBlock}
        <p className={BODY_CLASS}>{rest}</p>
      </>
    );
  }
  return <p className={className}>{children}</p>;
}

export function renderScopeAwareListItem(children, className = "leading-snug") {
  const text = plainTextFromChildren(children);
  const { head, rest } = splitScopeTitleBody(text);
  const match = head.match(SCOPE_ITEM_LINE) || scopeItemTitleMatch(head);
  if (match && rest) {
    return (
      <li className={className}>
        <div className="font-semibold text-[#001EFF]">
          <span>{match[1]}</span>
          <span className="mx-1 font-normal opacity-80">—</span>
          <span>{match[2]}</span>
        </div>
        <div className={`mt-0.5 font-light text-[var(--t-text)] whitespace-pre-wrap`}>{rest}</div>
      </li>
    );
  }
  if (match) {
    return (
      <li className={`${className} font-semibold text-[#001EFF]`}>
        {children}
      </li>
    );
  }
  return <li className={className}>{children}</li>;
}
